/**
 * SQL 验证：根据真实数据库表结构确认 IR 中数据集的 SQL 与字段声明是否正确。
 *
 * 设计原则：LLM 产出的 SQL 与字段是「猜测」的，必须通过服务端真实探查来确认。
 * 本模块对每个 type=sql 的数据集调用服务端 buildFields，拿到实际返回的字段，
 * 与 IR 中声明的 fields 逐项对比（字段名存在性、类型一致性），并捕获 SQL 执行错误。
 *
 * 参数报表（param-query）的 SQL 里含有 `${$param}` 占位符，服务端 buildFields 无法
 * 在未绑定参数时执行。校验阶段只关心「表/字段结构」，与查询条件无关，因此发送前
 * 先调用 `stripParamPlaceholders` 把占位符替换成中性字面量（null），既保留合法语法
 * 又能让服务端正常构建字段。
 *
 * 该能力依赖服务端连接（kind=server 的数据源需提供 datasource id），离线场景不适用。
 */

import type { ReportIR, DatasetIR, FieldType } from '../ir/types.js';
import { stripParamPlaceholders } from '../ir/types.js';
import type { Session } from '../../client/session.js';
import { buildFields, type TableSchema } from '../../client/datasourceApi.js';
import { suggestFromSchema } from './buildPromptSchema.js';

export interface SqlFieldIssue {
  level: 'error' | 'warn';
  dataset: string;
  message: string;
}

/** 服务端字段类型 → IR FieldType 的粗略映射。 */
function mapServerType(t?: string): FieldType | undefined {
  if (!t) return undefined;
  const s = t.toLowerCase();
  if (s.includes('int') || s.includes('decimal') || s.includes('numeric') ||
      s.includes('float') || s.includes('double') || s.includes('real') ||
      s.includes('money') || s === 'number' || s === 'bigint') return 'number';
  if (s.includes('date') || s.includes('time')) return 'date';
  if (s === 'boolean' || s === 'bit' || s === 'bool') return 'boolean';
  return 'string';
}

export interface VerifySqlOptions {
  /** 类型不匹配是否升级为 error（默认 warn）。 */
  strictType?: boolean;
  /** 遇到 SQL 执行失败时是否升级为 error（默认 error）。 */
  failOnSqlError?: boolean;
  /**
   * 真实数据库 schema（由 buildSchema 预先探查得到）。传入后，
   * 当 SQL 执行失败或声明字段缺失时，会自动附加「你是不是想写 X」的模糊建议，
   * 帮助 LLM/用户据此自动修正，而不是盲目重拟 SQL。
   * 不传则行为保持原有「仅报错」。
   */
  realSchema?: TableSchema[];
}

/**
 * 验证单个 SQL 数据集。返回该数据集相关的问题列表。
 * 若无法定位服务端数据源（缺 id/type），返回一条 warn 跳过。
 */
export async function verifyDatasetSql(
  session: Session,
  ds: DatasetIR,
  report: ReportIR,
  opts: VerifySqlOptions = {},
): Promise<SqlFieldIssue[]> {
  const issues: SqlFieldIssue[] = [];
  const failOnSqlError = opts.failOnSqlError ?? true;
  const strictType = opts.strictType ?? false;

  if (ds.type !== 'sql' || !ds.sql) return issues;

  // 找到关联的数据源，拿到服务端 id
  const dsRef = (report.datasources ?? []).find((d) => d.name === ds.datasource);
  const serverId = dsRef?.id;
  if (!serverId) {
    // 不能降级为 warn 放行：一旦跳过，SQL 里编造的表名/列名就完全无人拦截，
    // 只会在预览报表时才暴露。缺 id 属于报表写法问题，必须改用 kind=server 引用。
    issues.push({
      level: 'error',
      dataset: ds.name,
      message:
        `数据集「${ds.name}」关联的数据源「${ds.datasource}」缺少 kind=server 的 id，` +
        `无法按数据库真实表结构校验其 SQL。请改用服务端数据源引用（kind: "server" + id，` +
        `可用 datasource load-datasource-defs 查询），否则 SQL 中的表名/字段名无法被确认。`,
    });
    return issues;
  }

  // 调用服务端按 SQL 构建字段
  let realFields: { name?: string; type?: string }[] = [];
  // param-query 的 SQL 含 ${$param} 占位符，服务端无法在缺参数时执行；
  // 校验只关心表/字段结构，故先剥离占位符（替换为 null）再探查。
  const verifySql = stripParamPlaceholders(ds.sql);
  try {
    const res = await buildFields(session, String(serverId), verifySql, {
      tenantId: session.tenantId,
      limit: 1,
    });
    realFields = res.fields ?? [];
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    const fromClause = (ds.sql.match(/\bfrom\s+([a-zA-Z0-9_."`]+)/i) ?? [])[1];
    const joinClauses = [...ds.sql.matchAll(/\bjoin\s+([a-zA-Z0-9_."`]+)/gi)].map((m) => m[1]);
    const seedTables = [fromClause, ...joinClauses].filter(Boolean) as string[];
    const declaredNames = (ds.fields ?? []).map((f) => f.name).filter(Boolean) as string[];
    const suggest = opts.realSchema
      ? suggestFromSchema(opts.realSchema, seedTables, declaredNames)
      : undefined;
    let hint = '';
    if (suggest && (suggest.table?.length || Object.keys(suggest.fields).length)) {
      const parts: string[] = [];
      if (suggest.table?.length) parts.push(`可用表：${suggest.table.join(', ')}`);
      const fHints = Object.entries(suggest.fields).map(([k, v]) => `${k} → ${v.join('/')}`);
      if (fHints.length) parts.push(`字段近似：${fHints.join('；')}`);
      hint = ` 建议：${parts.join('；')}`;
    }
    issues.push({
      level: failOnSqlError ? 'error' : 'warn',
      dataset: ds.name,
      message: `数据集「${ds.name}」的 SQL 在数据库执行失败，无法确认表结构：${msg}${hint}`,
    });
    return issues;
  }

  if (realFields.length === 0) {
    // SQL 能执行却返回 0 列，通常意味着表名写错/表为空结构，属于硬错误而非提示。
    const fromClause = (ds.sql.match(/\bfrom\s+([a-zA-Z0-9_."`]+)/i) ?? [])[1];
    const suggest = opts.realSchema ? suggestFromSchema(opts.realSchema, [fromClause ?? ''].filter(Boolean), []) : undefined;
    const hint = suggest?.table?.length ? ` 可用表：${suggest.table.join(', ')}` : '';
    issues.push({
      level: 'error',
      dataset: ds.name,
      message: `数据集「${ds.name}」的 SQL 未返回任何字段（可能表不存在或查询无列），请检查 SQL：${ds.sql}${hint}`,
    });
  }

  const realNames = new Set(realFields.map((f) => (f.name ?? '').toLowerCase()));
  const declared = ds.fields ?? [];
  // 以「小写字段名」为键建立声明字段查找表，避免在每个 realField 循环内重复线性扫描与 toLowerCase 分配。
  const declaredByName = new Map<string, (typeof declared)[number]>();
  for (const f of declared) {
    if (!f.name) {
      issues.push({
        level: 'warn',
        dataset: ds.name,
        message: `数据集「${ds.name}」存在未命名（name 缺失）的字段声明，已跳过`,
      });
      continue;
    }
    declaredByName.set(f.name.toLowerCase(), f);
  }

  // 1. 声明字段是否都真实存在于 SQL 结果中
  for (const f of declared) {
    if (!f.name) continue; // 未命名字段已在上方告警
    if (!realNames.has(f.name.toLowerCase())) {
      const suggest = opts.realSchema
        ? suggestFromSchema(opts.realSchema, [], [f.name]).fields[f.name]
        : undefined;
      const hint = suggest?.length ? ` 你是不是想写：${suggest.join('/')}` : '';
      issues.push({
        level: 'error',
        dataset: ds.name,
        message: `字段「${f.name}」在数据集「${ds.name}」的 SQL 结果中不存在；SQL 实际返回字段：${realFields.map((r) => r.name).join(', ') || '(无)'}${hint}`,
      });
    }
  }

  // 2. SQL 返回的字段是否被声明（缺失声明 → warn，提示补全）
  for (const rf of realFields) {
    if (!rf.name) continue;
    const declaredField = declaredByName.get(rf.name.toLowerCase());
    if (!declaredField) {
      issues.push({
        level: 'warn',
        dataset: ds.name,
        message: `SQL 返回字段「${rf.name}」未在数据集「${ds.name}」中声明，建议补入 fields（cells 若引用将被忽略）`,
      });
      continue;
    }
    // 3. 类型一致性
    const realType = mapServerType(rf.type);
    if (realType && declaredField.type && realType !== declaredField.type) {
      issues.push({
        level: strictType ? 'error' : 'warn',
        dataset: ds.name,
        message: `字段「${rf.name}」类型不一致：IR 声明 ${declaredField.type}，数据库实际 ${rf.type ?? '未知'}（映射为 ${realType}）`,
      });
    }
  }

  return issues;
}

/** 验证整张报表所有 SQL 数据集。 */
export async function verifyReportSql(
  session: Session,
  report: ReportIR,
  opts: VerifySqlOptions = {},
): Promise<SqlFieldIssue[]> {
  const all: SqlFieldIssue[] = [];
  for (const ds of report.datasets ?? []) {
    if (ds.type !== 'sql') continue;
    const r = await verifyDatasetSql(session, ds, report, opts);
    all.push(...r);
  }
  return all;
}

/**
 * 带真实 schema 上下文的校验：先按报表里出现的所有服务端数据源探查表结构，
 * 再把 schema 注入 verifyReportSql，使校验失败时自动给出表名/字段模糊建议。
 *
 * 这是「避免 LLM 首次生成 SQL 对不上数据库」的兜底环节：
 * 即使 prompt 阶段没拿到 schema，这里也能让报错信息具备可修正性，
 * 而非只抛一条「构建字段失败」。
 *
 * 性能：仅对报表实际引用的服务端数据源各探查一次全量 schema，结果在数据集间复用。
 */
export async function verifyReportSqlWithSchema(
  session: Session,
  report: ReportIR,
  opts: VerifySqlOptions = {},
): Promise<SqlFieldIssue[]> {
  if (opts.realSchema) return verifyReportSql(session, report, opts);

  const ids = [
    ...new Set(
      (report.datasets ?? [])
        .filter((d) => d.type === 'sql' && d.sql)
        .map((d) => (report.datasources ?? []).find((s) => s.name === d.datasource)?.id)
        .filter((id): id is string => typeof id === 'string'),
    ),
  ];

  const schemaByLower = new Map<string, TableSchema[]>();
  for (const id of ids) {
    try {
      const { buildSchema } = await import('../../client/datasourceApi.js');
      const schema = await buildSchema(session, String(id), {
        tenantId: session.tenantId,
      });
      schemaByLower.set(String(id).toLowerCase(), schema);
    } catch {
      // 探查失败不阻断校验，仅失去「建议」能力。
    }
  }

  // 每个数据集用它关联数据源的 schema 去生成建议。
  const all: SqlFieldIssue[] = [];
  for (const ds of report.datasets ?? []) {
    if (ds.type !== 'sql') continue;
    const dsRef = (report.datasources ?? []).find((d) => d.name === ds.datasource);
    const schema = dsRef?.id ? schemaByLower.get(String(dsRef.id).toLowerCase()) : undefined;
    const r = await verifyDatasetSql(session, ds, report, { ...opts, realSchema: schema });
    all.push(...r);
  }
  return all;
}
